import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Select,
	Row,
	Col,
	Typography,
	notification,
	Popconfirm,
	Tooltip,
	Badge,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
	PiEyeDuotone,
	PiReceiptDuotone,
	PiHouseDuotone,
	PiCheckCircleDuotone,
	PiCalendarDuotone,
} from "react-icons/pi";
import AppContext from "../../store/app.context";
import SearchInput from "../../components/SearchInput";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";
import { requestGetInvoiceList } from "../../services/user.service";

const { Title, Text } = Typography;
const { Option } = Select;

function InvoicesListPage() {
	const context = useContext(AppContext);
	const { teamId } = useParams();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [invoices, setInvoices] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
		status: searchParams.get("status") || "",
	});

	// Helper function to build filter string from filters object
	const buildFilterString = (filtersObj) => {
		const filterParts = [];

		// Add status filter
		if (filtersObj.status) {
			filterParts.push(`status:${filtersObj.status}`);
		}

		// Add more filter fields here as needed
		// Example: if (filtersObj.issuedAt) filterParts.push(`issuedAt:${filtersObj.issuedAt}`);
		// Example: if (filtersObj.amount) filterParts.push(`amount:${filtersObj.amount}`);

		return filterParts.join(",");
	};

	useEffect(() => {
		document.title = "Cloudiva App | Faktur";
		fetchInvoices();
	}, []);

	useEffect(() => {
		fetchInvoices();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.keyword) params.set("keyword", filters.keyword);
		if (filters.status) params.set("status", filters.status);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchInvoices = () => {
		setLoading(true);

		// Build filter string from filter object using helper function
		const filterString = buildFilterString(filters);

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			filter: filterString,
			order: "issuedAt:desc",
		};

		requestGetInvoiceList(teamId, params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const invoicesData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setInvoices(invoicesData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				notification.error({
					message: "Gagal",
					description: error.message,
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleTableChange = (newPagination) => {
		setPagination(newPagination);
	};

	const handleSearch = (keyword) => {
		setFilters((prev) => ({ ...prev, keyword }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleStatusFilter = (status) => {
		setFilters((prev) => ({ ...prev, status }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const getStatusColor = (status) => {
		// DRAFT: 'DRAFT',
		// UNPAID: 'UNPAID',
		// PARTIALLY_PAID: 'PARTIALLY_PAID',
		// PAID: 'PAID',
		// CANCELLED: 'CANCELLED',
		// EXPIRED: 'EXPIRED',
		// REFUNDED: 'REFUNDED',
		// FAILED: 'FAILED',
		// VOIDED: 'VOIDED',
		switch (status) {
			case "DRAFT":
				return "default";
			case "UNPAID":
				return "error";
			case "PARTIALLY_PAID":
				return "warning";
			case "PAID":
				return "success";
			case "CANCELLED":
				return "error";
			case "EXPIRED":
				return "error";
			case "REFUNDED":
				return "error";
			case "FAILED":
				return "error";
			case "VOIDED":
				return "error";
			default:
				return "default";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "DRAFT":
				return "Draf";
			case "UNPAID":
				return "Belum Dibayar";
			case "PARTIALLY_PAID":
				return "Sebagian Dibayar";
			case "PAID":
				return "Lunas";
			case "CANCELLED":
				return "Dibatalkan";
			case "EXPIRED":
				return "Kedaluwarsa";
			case "REFUNDED":
				return "Dikembalikan";
			case "FAILED":
				return "Gagal";
			case "VOIDED":
				return "Tidak Sah";
			default:
				return status;
		}
	};

	const columns = [
		{
			title: "Nomor",
			dataIndex: "number",
			key: "number",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiReceiptDuotone size={20} style={{ marginRight: 8, color: "#1890ff" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							{record.order?.number && `Order: ${record.order.number}`}
						</div>
					</div>
				</div>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status, record) => (
				<Space size={2} direction="vertical">
					<Tag color={getStatusColor(status)} style={{ borderRadius: "12px" }}>
						{getStatusText(status)}
					</Tag>
					{status === "UNPAID" && dayjs(record.expiredAt).isBefore(dayjs().add(30, "day")) && (
						<Tag color="#ff4d4f" style={{ borderRadius: "12px" }}>
							Expiring in {dayjs(record.expiredAt).diff(dayjs(), "day")} days
						</Tag>
					)}
				</Space>
			),
		},
		{
			title: "Total",
			dataIndex: "total",
			key: "total",
			render: (amount, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<div>
						<div style={{ fontWeight: 500 }}>{formatCurrency(amount, record.currency)}</div>
						{record.discountTotal > 0 && (
							<div style={{ fontSize: "12px", color: "#666" }}>
								Discount: {formatCurrency(record.discountTotal, record.currency)}
							</div>
						)}
					</div>
				</div>
			),
		},
		{
			title: "Mata Uang",
			dataIndex: "currency",
			key: "currency",
			render: (currency) => <Tag color="blue">{currency}</Tag>,
		},
		{
			title: "Data Pelanggan",
			dataIndex: "billingAddress",
			key: "billingAddress",
			render: (billingAddress) => (
				<div>
					<div style={{ fontWeight: 500 }}>{billingAddress?.name}</div>
					<div style={{ fontSize: "12px", color: "#666" }}>{billingAddress?.email}</div>
				</div>
			),
		},
		{
			title: "Tanggal",
			key: "dates",
			render: (_, record) => (
				<div>
					<div style={{ fontSize: "12px" }}>
						<PiCalendarDuotone color="#52c41a" />{" "}
						{dayjs(record.issuedAt).format("DD MMMM YYYY HH:mm")}
					</div>
					<div style={{ fontSize: "12px", color: "#666" }}>
						<PiCalendarDuotone color="#ff4d4f" />{" "}
						{dayjs(record.expiredAt).format("DD MMMM YYYY HH:mm")}
					</div>
				</div>
			),
		},
		{
			title: "Aksi",
			key: "action",
			align: "right",
			render: (_, record) => (
				<Space size="middle">
					<Tooltip title="View Invoice">
						<Button
							type="text"
							size="small"
							icon={<PiEyeDuotone />}
							onClick={() => navigate(`/${teamId}/invoices/${record._id}`)}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	// Expandable row render for invoice items
	const expandedRowRender = (record) => {
		const itemColumns = [
			{
				title: "Layanan",
				dataIndex: ["service", "name"],
				key: "service",
				render: (text, item) => (
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>{item.service?.code}</div>
					</div>
				),
			},
			{
				title: "Paket",
				dataIndex: ["plan", "name"],
				key: "plan",
				render: (text, item) => (
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>{item.plan?.code}</div>
					</div>
				),
			},
			{
				title: "Region",
				dataIndex: ["region", "name"],
				key: "region",
				render: (text) => text || "N/A",
			},
			{
				title: "Kuantitas",
				dataIndex: "quantity",
				key: "quantity",
				render: (quantity, item) => (
					<div>
						<div>{quantity}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>{item.plan?.price?.interval}</div>
					</div>
				),
			},
			{
				title: "Subtotal",
				dataIndex: "subtotal",
				key: "subtotal",
				render: (subtotal) => formatCurrency(subtotal, record.currency),
			},
		];

		return (
			<Table
				columns={itemColumns}
				dataSource={record.items}
				pagination={false}
				size="small"
				rowKey={(item, index) => `${record._id}-${index}`}
			/>
		);
	};

	return (
		<div className="page-content">
			<div className="page-content-wrapper">
				<Card
					title={
						<Space>
							Tagihan Anda
							<Badge count={pagination.total} showZero></Badge>
						</Space>
					}>
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={6}>
							<SearchInput
								placeholder="Cari faktur..."
								onSearch={handleSearch}
								style={{ width: "100%" }}
							/>
						</Col>
						<Col span={4}>
							<Select
								placeholder="Status"
								onChange={handleStatusFilter}
								allowClear
								style={{ width: "100%" }}>
								<Option value="UNPAID">Belum Dibayar</Option>
								<Option value="PARTIALLY_PAID">Sebagian Dibayar</Option>
								<Option value="PAID">Lunas</Option>
								<Option value="CANCELLED">Dibatalkan</Option>
								<Option value="EXPIRED">Kedaluwarsa</Option>
								<Option value="REFUNDED">Dikembalikan</Option>
								<Option value="FAILED">Gagal</Option>
								<Option value="VOIDED">Dibatalkan</Option>
							</Select>
						</Col>
					</Row>
					<Table
						columns={columns}
						dataSource={invoices}
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} faktur`,
						}}
						onChange={handleTableChange}
						expandable={{
							expandedRowRender,
							rowExpandable: (record) => record.items && record.items.length > 0,
						}}
						rowKey="_id"
					/>
				</Card>
			</div>
		</div>
	);
}

export default InvoicesListPage;
